/**
 * Every figure that goes into one party's net monthly income, itemised, so a screen can show its
 * working. Mirrors `calcSourcesAndAmounts` in the worksheet calcs (the gross annual income) and
 * the three tax liabilities `computeNetMonthlyIncome` already reads; no React, no engine.
 *
 * Third-party alimony (paid to or received from someone other than the other party) is real
 * income and is itemised here. Spousal support between the two parties is never included: the
 * calculation this reads is run with it removed (see alimonyAnalysis.ts `computeBaseline`).
 */

import type { CaseData } from '../types/case';

export interface BreakdownLine {
    label: string;
    /** Monthly, signed: a deduction or an amount paid out is negative. */
    monthly: number;
    /** A short qualifier shown beside the label. */
    note?: string;
}

export interface PartyIncomeBreakdown {
    /** Income sources, before tax. */
    income: BreakdownLine[];
    /** The sum of `income`. Equals the worksheet's gross income / 12. */
    grossMonthly: number;
    /** Federal, state and FICA / Medicare (negative). */
    taxes: BreakdownLine[];
    netMonthly: number;
}

export interface PartyFigures {
    grossMonthly: number;
    federalTax: number;
    stateTax: number;
    socialSecurity: number;
    netMonthly: number;
}

const CENT = 0.005;

const sum = <T,>(items: readonly T[] | undefined, pick: (item: T) => number): number =>
    (items ?? []).reduce((total, item) => total + pick(item), 0);

/**
 * @param caseData the CALCULATED case the figures came from (its party inputs are what is itemised)
 * @param party which party's inputs to read
 * @param figures that party's gross / tax / net figures, from the worksheet
 */
export function computeNetIncomeBreakdown(
    caseData: CaseData | null | undefined,
    party: 'partyA' | 'partyB',
    figures: PartyFigures,
): PartyIncomeBreakdown {
    const p = caseData?.parties?.[party];
    const lines: BreakdownLine[] = [];
    const add = (label: string, annual: number, note?: string) => {
        if (Math.abs(annual) < CENT) return;
        lines.push({ label, monthly: annual / 12, note });
    };

    add('Wages', p?.income?.primary?.yearlyWages ?? 0);
    add('Self-employment income', p?.income?.primary?.yearlySelfEmployment ?? 0);
    (p?.income?.others ?? []).forEach((other, index) => {
        add(other?.name?.trim() || `Other income ${index + 1}`, other?.amount ?? 0);
    });

    const pensionsOutsideWages = sum(p?.pensions, (x) =>
        x?.isIncludedInGrossWages === false ? (x?.computedAmount ?? (x?.income ?? 0) * (x?.rate ?? 0)) : 0);
    add('Retirement contributions not in gross wages', Math.abs(pensionsOutsideWages));

    add('Child care reimbursements', p?.deductions?.childCareReimbursements ?? 0);

    // Amounts are entered monthly; the worksheet annualises them, so the monthly figure is the entry.
    const thirdParty = (kind: 'receive' | 'pay') => Math.abs(sum(p?.spousalSupports, (x) =>
        x?.otherParty === 'thirdParty' && x?.payOrReceive === kind ? (x?.amount ?? 0) : 0));
    add('Alimony received from a third party', thirdParty('receive') * 12);
    add('Alimony paid to a third party', -thirdParty('pay') * 12);

    // Whatever the itemised lines do not reach: the alternating-year average, or income in a form
    // this list does not know about. Shown, so the column always adds up to the gross figure.
    const itemised = lines.reduce((total, line) => total + line.monthly, 0);
    const remainder = figures.grossMonthly - itemised;
    if (Math.abs(remainder) >= CENT) {
        lines.push({ label: 'Other adjustments', monthly: remainder, note: 'for example the average over alternating tax years' });
    }

    return {
        income: lines,
        grossMonthly: figures.grossMonthly,
        taxes: [
            { label: 'Federal income tax', monthly: figures.federalTax },
            { label: 'State income tax', monthly: figures.stateTax },
            { label: 'Social Security and Medicare (FICA)', monthly: figures.socialSecurity },
        ],
        netMonthly: figures.netMonthly,
    };
}
